import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const Header = () => {
  const { t, i18n } = useTranslation();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <motion.header 
      className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700 relative overflow-hidden"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Enhanced Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 via-purple-900/10 to-green-900/20" />
      
      {/* Animated Color Accents */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-0 left-1/4 w-32 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 blur-sm"
          animate={{
            x: [0, 100, 0],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-0 right-1/4 w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 blur-sm"
          animate={{
            x: [0, -100, 0],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-4 relative z-10">
        <div className="flex items-center justify-between">
          {/* Enhanced Logo */}
          <motion.div 
            className="text-2xl font-bold text-white cursor-pointer relative"
            onClick={() => scrollToSection('hero')}
            whileHover={{ 
              scale: 1.02,
            }}
            transition={{ duration: 0.2 }}
          >
            <motion.span
              style={{
                background: "linear-gradient(135deg, #ffffff, #60a5fa, #34d399)",
                backgroundSize: "200% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              HOKS
            </motion.span>
            
            {/* Logo Underline */}
            <motion.div
              className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-green-500 rounded-full opacity-0"
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>

          {/* Enhanced Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {[
              { key: 'about', section: 'about', color: 'from-blue-500 to-cyan-500' },
              { key: 'services', section: 'services', color: 'from-green-500 to-emerald-500' },
              { key: 'advantages', section: 'advantages', color: 'from-purple-500 to-pink-500' },
              { key: 'contact', section: 'contact', color: 'from-orange-500 to-red-500' }
            ].map((item, index) => (
              <motion.button
                key={item.key}
                onClick={() => scrollToSection(item.section)}
                className="text-slate-300 hover:text-white transition-colors duration-300 relative group"
                whileHover={{ y: -1 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
              >
                <span className="relative z-10">{t(item.key)}</span>
                
                {/* Enhanced Colored Underline */}
                <motion.div
                  className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${item.color} rounded-full`}
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
                
                {/* Glow Effect */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-md opacity-0 blur-sm`}
                  whileHover={{ opacity: 0.1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            ))}
          </nav>

          {/* Enhanced Language Switcher */}
          <div className="flex items-center space-x-2">
            <motion.button
              onClick={() => changeLanguage('uk')}
              className={`px-3 py-1 text-sm rounded-md transition-all duration-300 relative overflow-hidden ${
                i18n.language === 'uk' 
                  ? 'text-white border border-blue-500 shadow-lg shadow-blue-500/25' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {i18n.language === 'uk' && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500"
                  layoutId="activeLanguage"
                  transition={{ duration: 0.3 }}
                />
              )}
              <span className="relative z-10">УК</span>
            </motion.button>
            
            <motion.button
              onClick={() => changeLanguage('en')}
              className={`px-3 py-1 text-sm rounded-md transition-all duration-300 relative overflow-hidden ${
                i18n.language === 'en' 
                  ? 'text-white border border-green-500 shadow-lg shadow-green-500/25' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {i18n.language === 'en' && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500"
                  layoutId="activeLanguage"
                  transition={{ duration: 0.3 }}
                />
              )}
              <span className="relative z-10">EN</span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;